// usePlayer — the single hook the UI uses to drive playback. Reads the live MusicKit
// snapshot via useSyncExternalStore (so the persistent banner and Now Playing update in
// lock-step across routes) and exposes provider-neutral controls. No component imports the
// provider or MusicKit directly.
import { useCallback, useSyncExternalStore } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { appleMusicProvider } from '../lib/appleMusicProvider';
import type { MusicItem, MusicPlayableItem, NowPlayingSnapshot } from '../lib/types';
import { setAuthState, openPanel, togglePanel, setCollapsed, setError, type PlayerPanel } from './playerSlice';

const provider = appleMusicProvider();
const EMPTY: NowPlayingSnapshot = { item: null, status: 'idle', positionMs: 0, durationMs: 0, shuffle: false, repeat: 'off' };

// A stable snapshot cache so useSyncExternalStore doesn't loop (it compares by reference).
let cache: NowPlayingSnapshot = EMPTY;
function getSnapshot(): NowPlayingSnapshot {
  const next = provider.snapshot();
  // Compare metadata too (title/artist/artwork), not just id — MusicKit can advance to a new
  // track whose id/duration momentarily match the cached one, which left the banner stale.
  const same = cache.item?.id === next.item?.id && cache.item?.title === next.item?.title &&
    cache.item?.artist === next.item?.artist && cache.item?.artwork?.url === next.item?.artwork?.url &&
    cache.status === next.status && cache.positionMs === next.positionMs && cache.durationMs === next.durationMs &&
    cache.shuffle === next.shuffle && cache.repeat === next.repeat;
  if (!same) cache = next;
  return cache;
}

export function usePlayerSnapshot(): NowPlayingSnapshot {
  return useSyncExternalStore((cb) => provider.subscribe(cb), getSnapshot, () => EMPTY);
}

export function usePlayer() {
  const dispatch = useAppDispatch();
  const ui = useAppSelector((s) => (s as any).player) as import('./playerSlice').PlayerUiState;
  const snap = usePlayerSnapshot();

  const guard = useCallback(async (fn: () => Promise<unknown>) => {
    try { await fn(); dispatch(setError(null)); }
    catch (e: any) {
      if (e?.message === 'no_subscription') { dispatch(setAuthState('connected_no_subscription')); toast.error(getApiErrorMessage(e, 'Playback needs an active Apple Music subscription.')); }
      else { dispatch(setError('Playback error')); toast.error(getApiErrorMessage(e, 'Could not play that right now.')); }
    }
  }, [dispatch]);

  const refreshAuth = useCallback(async () => {
    const st = await provider.getAuthorizationState();
    dispatch(setAuthState(st));
    return st;
  }, [dispatch]);

  const connect = useCallback(() => guard(async () => { const st = await provider.authorize(); dispatch(setAuthState(st)); }), [guard, dispatch]);
  const disconnect = useCallback(() => guard(async () => { await provider.disconnect(); dispatch(setAuthState('not_connected')); }), [guard, dispatch]);

  const playItem = useCallback((item: MusicItem) => {
    const kind = item.kind === 'artist' ? 'song' : (item.kind as MusicPlayableItem['kind']);
    return guard(() => provider.play({ id: item.id, kind }));
  }, [guard]);

  const toggle = useCallback(() => guard(() => (snap.status === 'playing' ? provider.pause() : provider.resume())), [guard, snap.status]);

  return {
    snap, ui, provider,
    connect, disconnect, refreshAuth, playItem, toggle,
    pause: () => guard(() => provider.pause()),
    resume: () => guard(() => provider.resume()),
    next: () => guard(() => provider.skipNext()),
    prev: () => guard(() => provider.skipPrevious()),
    seek: (sec: number) => guard(() => provider.seek(sec)),
    setQueue: (items: MusicItem[], startIndex = 0, startPlaying = true) =>
      guard(() => provider.setQueue(items.map((i) => ({ id: i.id, kind: 'song' as const })), startIndex, startPlaying)),
    /** Re-queue a saved session after a reload: same tracks and position, left paused. */
    restoreQueue: async (items: MusicItem[], startIndex: number, positionMs: number) => {
      await provider.setQueue(items.map((i) => ({ id: i.id, kind: 'song' as const })), startIndex, false);
      if (positionMs > 1000) { try { await provider.seek(Math.floor(positionMs / 1000)); } catch { /* best effort */ } }
    },
    shuffle: (on: boolean) => guard(() => provider.setShuffle(on)),
    repeat: (mode: 'off' | 'one' | 'all') => guard(() => provider.setRepeat(mode)),
    volume: (v: number) => guard(() => provider.setVolume(v)),
    openPanel: (p: PlayerPanel) => dispatch(openPanel(p)),
    togglePanel: (p: Exclude<PlayerPanel, 'none'>) => dispatch(togglePanel(p)),
    setCollapsed: (c: boolean) => dispatch(setCollapsed(c)),
  };
}
